import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { marketplaceCategories } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { ArrowLeft, Upload, ImageIcon } from "lucide-react";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import { RegionAutocomplete, type RegionDetails } from "@/components/RegionAutocomplete";

const regions = [
  { id: "ALL", label: "전체 지역" },
  { id: "NSW", label: "NSW (뉴사우스웨일스)" },
  { id: "VIC", label: "VIC (빅토리아)" },
  { id: "QLD", label: "QLD (퀸즐랜드)" },
  { id: "SA", label: "SA (남호주)" },
  { id: "WA", label: "WA (서호주)" },
  { id: "TAS", label: "TAS (태즈메이니아)" },
  { id: "NT", label: "NT (북부 준주)" },
  { id: "ACT", label: "ACT (수도 준주)" },
];

export default function MarketplaceRegister() {
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [regionSearchValue, setRegionSearchValue] = useState("");

  const [formData, setFormData] = useState({
    businessName: "",
    category: "",
    region: "ALL",
    title: "",
    titleKo: "",
    description: "",
    descriptionKo: "",
    originalPrice: "",
    salePrice: "",
    imageUrl: "",
    imageUrls: [] as string[],
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    website: "",
    address: "",
    city: "",
    state: "",
    postcode: "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegionAutocomplete = (value: string, details?: RegionDetails) => {
    setRegionSearchValue(value);
    if (details) {
      const stateMapping: Record<string, string> = {
        'NSW': 'NSW',
        'VIC': 'VIC',
        'QLD': 'QLD',
        'SA': 'SA',
        'WA': 'WA',
        'TAS': 'TAS',
        'NT': 'NT',
        'ACT': 'ACT',
      };
      
      const regionCode = stateMapping[details.state];
      if (regionCode) {
        handleChange("region", regionCode);
        handleChange("city", details.suburb || details.city);
        handleChange("state", details.state);
        handleChange("postcode", details.postcode);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !user) {
      toast({
        title: "로그인 필요",
        description: "상품을 등록하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      return;
    }

    // 필수 필드 검증
    if (!formData.businessName || !formData.category || !formData.title || 
        !formData.description || !formData.salePrice || !formData.contactPhone) {
      toast({
        title: "필수 정보 누락",
        description: "모든 필수 항목을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        originalPrice: formData.originalPrice ? formData.originalPrice : null,
        salePrice: formData.salePrice,
        discountRate: formData.originalPrice && formData.salePrice
          ? Math.round(((parseFloat(formData.originalPrice) - parseFloat(formData.salePrice)) / parseFloat(formData.originalPrice)) * 100)
          : null,
        imageUrl: formData.imageUrls[0] || "", // 첫 번째 이미지를 대표 이미지로
        imageUrls: formData.imageUrls, // 모든 이미지 배열 전송
        userId: user.uid,
        status: "pending", // 관리자 승인 대기
      };

      await apiRequest("POST", "/api/marketplace/items", payload);

      toast({
        title: "등록 완료",
        description: "상품이 등록되었습니다. 관리자 승인 후 게시됩니다.",
      });

      setLocation("/marketplace");
    } catch (error: any) {
      toast({
        title: "등록 실패",
        description: error.message || "상품 등록 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>로그인 필요</CardTitle>
            <CardDescription>
              상품을 등록하려면 로그인이 필요합니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setLocation("/")}>홈으로 돌아가기</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        variant="ghost"
        onClick={() => setLocation("/marketplace")}
        className="mb-4"
        data-testid="button-back"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        돌아가기
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>상품/서비스 등록</CardTitle>
          <CardDescription>
            업체 정보와 상품 정보를 입력해주세요. 관리자 승인 후 게시됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 업체 기본 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">업체 정보</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="businessName">업체명 *</Label>
                  <Input
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => handleChange("businessName", e.target.value)}
                    placeholder="예: 서울 한식당"
                    required
                    data-testid="input-businessName"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">카테고리 *</Label>
                  <Select value={formData.category} onValueChange={(val) => handleChange("category", val)}>
                    <SelectTrigger id="category" data-testid="select-category">
                      <SelectValue placeholder="카테고리 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {marketplaceCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>지역 빠른 검색 (자동완성)</Label>
                  <RegionAutocomplete
                    value={regionSearchValue}
                    onChange={handleRegionAutocomplete}
                    placeholder="지역명이나 우편번호를 입력하세요 (예: Epping, 2121)"
                    label=""
                  />
                  <p className="text-xs text-muted-foreground">
                    또는 아래에서 직접 선택하세요
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">주 (State) *</Label>
                  <Select value={formData.region} onValueChange={(val) => handleChange("region", val)}>
                    <SelectTrigger id="region" data-testid="select-region">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* 상품/서비스 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">상품/서비스 정보</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">상품명 (영문) *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="Korean BBQ Special"
                    required
                    data-testid="input-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titleKo">상품명 (한글)</Label>
                  <Input
                    id="titleKo"
                    value={formData.titleKo}
                    onChange={(e) => handleChange("titleKo", e.target.value)}
                    placeholder="한우 특선 세트"
                    data-testid="input-titleKo"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">상품 설명 (영문) *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="상품에 대한 자세한 설명을 입력하세요"
                  rows={4}
                  required
                  data-testid="textarea-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descriptionKo">상품 설명 (한글)</Label>
                <Textarea
                  id="descriptionKo"
                  value={formData.descriptionKo}
                  onChange={(e) => handleChange("descriptionKo", e.target.value)}
                  placeholder="상품에 대한 자세한 설명을 입력하세요"
                  rows={4}
                  data-testid="textarea-descriptionKo"
                />
              </div>

              {/* Images Section */}
              <div className="space-y-4 p-4 border rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-5 h-5" />
                  <h4 className="font-semibold">상품 사진</h4>
                </div>
                
                <MarketImageUploader
                  maxImages={10}
                  onImagesChange={(paths) => setFormData(prev => ({ ...prev, imageUrls: paths }))}
                  userId={user?.uid}
                />
                
                <p className="text-xs text-muted-foreground">
                  상품과 관련된 사진을 최대 10장까지 등록할 수 있어요.
                  <br />
                  첫 번째 사진이 대표 이미지로 보여져요.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="originalPrice">정가 ($)</Label>
                  <Input
                    id="originalPrice"
                    type="number"
                    step="0.01"
                    value={formData.originalPrice}
                    onChange={(e) => handleChange("originalPrice", e.target.value)}
                    placeholder="100.00"
                    data-testid="input-originalPrice"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salePrice">판매가 ($) *</Label>
                  <Input
                    id="salePrice"
                    type="number"
                    step="0.01"
                    value={formData.salePrice}
                    onChange={(e) => handleChange("salePrice", e.target.value)}
                    placeholder="80.00"
                    required
                    data-testid="input-salePrice"
                  />
                </div>
              </div>
            </div>

            {/* 연락처 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">연락처 정보</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactName">담당자명</Label>
                  <Input
                    id="contactName"
                    value={formData.contactName}
                    onChange={(e) => handleChange("contactName", e.target.value)}
                    placeholder="홍길동"
                    data-testid="input-contactName"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">연락처 *</Label>
                  <Input
                    id="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => handleChange("contactPhone", e.target.value)}
                    placeholder="02-1234-5678"
                    required
                    data-testid="input-contactPhone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">이메일</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleChange("contactEmail", e.target.value)}
                    placeholder="contact@example.com"
                    data-testid="input-contactEmail"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">웹사이트</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange("website", e.target.value)}
                    placeholder="https://example.com"
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>

            {/* 주소 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">주소 (선택사항)</h3>
              
              <div className="space-y-2">
                <Label htmlFor="address">상세 주소</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="123 Main Street"
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">도시</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="Sydney"
                    data-testid="input-city"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">주/구역</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                    placeholder="NSW"
                    data-testid="input-state"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postcode">우편번호</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => handleChange("postcode", e.target.value)}
                    placeholder="2000"
                    data-testid="input-postcode"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading}
                data-testid="button-submit"
              >
                {loading ? "등록 중..." : "상품 등록"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setLocation("/marketplace")}
                data-testid="button-cancel"
              >
                취소
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              * 표시는 필수 입력 항목입니다. 등록 후 관리자 승인이 필요합니다.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
